import { IScheduledPdfCustomReport } from '@/Interfaces/PdfBuilder/ScheduledPdfCustomReport.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import GridToolBar from '@/pages/ReportBuilder/Components/GridToolbar';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { GridPaginationModel, GridSortModel, gridClasses } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getColumns } from './columns';

interface IProps {
  data: IScheduledPdfCustomReport[];
  loading?: boolean;
  rowCount: number;
  loadScheduledReports: (payload?: ICustomReportListRequestPayload) => void;
  onEdit: (report: IScheduledPdfCustomReport) => void;
  onCancel: (id: number) => void;
}

/**
 + * Server-side paginated and sorted data grid for scheduled PDF reports.
 + * Manages pagination/sort state and triggers parent callbacks to load data.
 + */
const ScheduledReportsGrid = ({ data, loading, rowCount, loadScheduledReports, onEdit, onCancel }: IProps) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15,
  });

  const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
  }, []);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  useEffect(() => {
    loadScheduledReports({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize, sort: sortModel });
  }, [loadScheduledReports, paginationModel, sortModel]);

  const columns = useMemo(() => getColumns({ onEdit, onCancel }), [onEdit, onCancel]);

  return (
    <Grid
      sx={{
        border: 'none',
        [`& .${gridClasses.main}`]: {
          borderBottom: '1px solid rgb(227, 227, 227)',
        },
        [`& .${gridClasses.columnHeaders}`]: {
          borderRadius: 0,
          borderTop: '1px solid rgb(227, 227, 227)',
        },
      }}
      columns={columns}
      loading={loading}
      autosizeOnMount
      autosizeOptions={EXPAND_OPTIONS}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      showCellVerticalBorder
      showColumnVerticalBorder
      rows={data}
      columnHeaderHeight={30}
      sortingMode='server'
      paginationMode='server'
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      pagination
      slots={{
        // @ts-expect-error ServerSidePagination requires rowCount; MUI slot type only provides Partial<TablePaginationProps>
        pagination: ServerSidePagination,
        // @ts-expect-error GridToolBar accepts custom loading/title props not in GridToolbarProps
        toolbar: GridToolBar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
      }}
      slotProps={{
        pagination: {
          // @ts-expect-error loading prop not in PaginationPropsOverrides
          loading,
          rowCount,
        },
        footer: { className: 'relative' },
        // @ts-expect-error loading prop not in ToolbarPropsOverrides
        toolbar: { loading, title: 'Scheduled Reports' },
      }}
    />
  );
};

export default ScheduledReportsGrid;
